import { MaterialPicker } from '@/components/sklad/MaterialPicker';
import type { Material } from '@/components/sklad/materialTypes';

const INPUT =
  'w-full text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

/** Řádek formuláře objednávky = jedna položka drobného materiálu + množství. */
export type MaterialFormRow = { key: string; materialId: number | null; quantity: string };

export function newMaterialRow(): MaterialFormRow {
  return { key: crypto.randomUUID(), materialId: null, quantity: '' };
}

/** Sekce „Drobný materiál" ve formuláři objednávky (vedle rolí látek). */
export function MaterialOrderRows({
  rows,
  catalog,
  loading,
  onChange,
}: {
  rows: MaterialFormRow[];
  catalog: Material[];
  loading: boolean;
  onChange: (rows: MaterialFormRow[]) => void;
}) {
  const byId = new Map(catalog.map((m) => [m.id, m]));

  function update(key: string, patch: Partial<MaterialFormRow>) {
    const idx = rows.findIndex((r) => r.key === key);
    const wasEmpty = idx >= 0 && rows[idx]?.materialId === null;
    const next = rows.map((r) => (r.key === key ? { ...r, ...patch } : r));
    // Výběr v posledním prázdném řádku rovnou přidá další — stejně jako u látek.
    if (patch.materialId && idx === rows.length - 1 && wasEmpty) next.push(newMaterialRow());
    onChange(next);
  }

  function remove(key: string) {
    const next = rows.filter((r) => r.key !== key);
    onChange(next.length > 0 ? next : [newMaterialRow()]);
  }

  return (
    <div className="mt-5">
      <span className="text-sm font-medium text-text-secondary block mb-2">Drobný materiál</span>
      <div className="space-y-2">
        {rows.map((r, idx) => {
          const m = r.materialId !== null ? (byId.get(r.materialId) ?? null) : null;
          return (
            <div
              key={r.key}
              className="flex items-start gap-2 bg-bg rounded-lg border border-border p-2.5"
            >
              <span className="text-sm font-semibold text-text-muted w-5 text-right pt-2 shrink-0">
                {idx + 1}.
              </span>
              <div className="flex-1 min-w-0">
                <MaterialPicker
                  value={m}
                  options={catalog}
                  loading={loading}
                  onChange={(sel) => update(r.key, { materialId: sel.id })}
                />
              </div>
              <div className="flex items-center gap-1 w-32 shrink-0 pt-0.5">
                <input
                  value={r.quantity}
                  onChange={(e) => update(r.key, { quantity: e.target.value })}
                  inputMode="decimal"
                  placeholder="Množství"
                  className={`${INPUT} text-right`}
                />
                <span className="text-xs text-text-muted w-4">{m?.unit ?? 'ks'}</span>
              </div>
              <button
                type="button"
                onClick={() => remove(r.key)}
                className="text-text-muted hover:text-danger px-1 pt-2 cursor-pointer shrink-0"
                title="Odebrat položku"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => onChange([...rows, newMaterialRow()])}
        className="text-sm font-semibold text-primary hover:text-primary-dark mt-2 cursor-pointer"
      >
        + Přidat materiál
      </button>
    </div>
  );
}
